import * as React from 'react';

import { useImmer } from 'use-immer';

import { QuizItemCache } from '../../../../api/quiz/getQuizzes/normalize/toQuizItemCache';
import { QuizOrFolder } from '../../../../api/quiz/getQuizzes/types';

interface UseFilteredSortedQuizListItemsParams {
  quizListItems: QuizItemCache;
  filter: (items: QuizItemCache) => QuizOrFolder[];
  sort: (items: QuizOrFolder[]) => QuizOrFolder[];
}

export function useFilteredSortedQuizListItems({
  quizListItems,
  filter,
  sort,
}: UseFilteredSortedQuizListItemsParams) {
  const [selectedQuizItem, setSelectedQuizItem] = useImmer<Set<string>>(
    new Set<string>()
  );

  const quizOrFolders = React.useMemo(
    () => (!quizListItems ? [] : sort(filter(quizListItems))),
    [sort, filter, quizListItems]
  );

  const allPossibleIds = React.useMemo(
    () =>
      new Set(
        quizOrFolders
          .filter((qof) => !(qof.locked === true))
          .map((qof) => qof.uniqueId)
      ),
    [quizOrFolders]
  );

  const isSelectedAll = React.useMemo(
    () =>
      selectedQuizItem.size === 0
        ? false
        : selectedQuizItem.size === allPossibleIds.size
        ? true
        : 'mixed',
    [selectedQuizItem, allPossibleIds]
  );

  const selectAll = React.useCallback(() => {
    setSelectedQuizItem(new Set(allPossibleIds));
  }, [setSelectedQuizItem, allPossibleIds]);

  const selectNone = React.useCallback(() => {
    setSelectedQuizItem(new Set());
  }, [setSelectedQuizItem]);

  const toggleSelectAll = React.useCallback(
    () => (isSelectedAll ? selectNone() : selectAll()),
    [selectAll, selectNone, isSelectedAll]
  );

  const addSelectedItem = React.useCallback(
    (uniqueId: string) =>
      setSelectedQuizItem((draft) => {
        draft.add(uniqueId);
      }),
    [setSelectedQuizItem]
  );

  const removeSelectedItem = React.useCallback(
    (uniqueId: string) => {
      setSelectedQuizItem((draft) => {
        draft.delete(uniqueId);
      });
    },
    [setSelectedQuizItem]
  );

  const toggleItem = React.useCallback(
    (uniqueId: string) => {
      if (selectedQuizItem.has(uniqueId)) {
        removeSelectedItem(uniqueId);
      } else {
        addSelectedItem(uniqueId);
      }
    },
    [selectedQuizItem, removeSelectedItem, addSelectedItem]
  );

  /** Lots of things (Deletion, restoration, purging etc)
   * could happen where a quiz item id is no longer a valid selection.
   * This ensures that non selectable quizzes are not marked as selected.
   * Be careful editing this, since it's pretty easy to accidentally trigger an
   * infinite loop.**/
  React.useEffect(() => {
    selectedQuizItem.forEach((selectedQuizItem) => {
      if (!allPossibleIds.has(selectedQuizItem)) {
        // Ensure this *only* gets updated if it needs to.
        setSelectedQuizItem((draft) => {
          draft.delete(selectedQuizItem);
        });
      }
    });
  }, [allPossibleIds, selectedQuizItem, setSelectedQuizItem]);

  return {
    quizOrFolders: quizOrFolders,
    isSelectedAll: isSelectedAll,
    selectedQuizItem: selectedQuizItem,
    toggleSelectAll: toggleSelectAll,
    addSelectedItem: addSelectedItem,
    removeSelectedItem: removeSelectedItem,
    toggleItem: toggleItem,
    selectAll: selectAll,
    selectNone: selectNone,
    setSelectedQuizItem: setSelectedQuizItem,
  } as const;
}
